import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { MEALS, WEEK } from "@/constants/testIds";
import { Sparkles, ShoppingCart, RefreshCw, ChefHat, CalendarDays, CalendarRange, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { hasPremiumAccess } from "@/lib/subscription";
import FoodPrefsEditor from "@/components/FoodPrefsEditor";

export default function Meals() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("day"); // "day" | "week"
  const [plan, setPlan] = useState(null);
  const [weekPlan, setWeekPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [swapping, setSwapping] = useState(null);
  const [activeDay, setActiveDay] = useState(0);

  // Premium AI meal generation is gated for non-subscribed clients so the
  // marketing promise ("AI meal planning is Premium") isn't a lie. Admins
  // and coaches bypass at the backend for QA/preview purposes. Cancelled
  // subscribers keep access until the end of their paid billing cycle.
  const isPremium = hasPremiumAccess(user);

  const load = async () => {
    try {
      const [d, w] = await Promise.all([
        api.get("/meals/latest").catch(() => ({ data: null })),
        api.get("/meals/latest_week").catch(() => ({ data: null })),
      ]);
      setPlan(d.data);
      setWeekPlan(w.data);
    } catch {}
  };
  useEffect(() => {
    load();
  }, []);

  const handleGenError = (e) => {
    const detail = e?.response?.data?.detail || "Generation failed";
    if (e?.response?.status === 402) {
      toast.error(detail, {
        duration: 8000,
        action: { label: "Upgrade", onClick: () => navigate("/pricing") },
      });
    } else {
      toast.error(detail);
    }
  };

  const generateDay = async () => {
    setLoading(true);
    try {
      const r = await api.post("/meals/generate");
      setPlan(r.data);
      setView("day");
      toast.success("Daily plan ready");
    } catch (e) {
      handleGenError(e);
    } finally {
      setLoading(false);
    }
  };

  const generateWeek = async () => {
    setLoading(true);
    try {
      const r = await api.post("/meals/generate_week");
      setWeekPlan(r.data);
      setView("week");
      toast.success("7-day plan ready");
    } catch (e) {
      handleGenError(e);
    } finally {
      setLoading(false);
    }
  };

  const swap = async (idx, meal) => {
    setSwapping(idx);
    try {
      const r = await api.post("/meals/swap", { meal });
      const updated = { ...plan };
      updated.plan = { ...updated.plan, meals: [...updated.plan.meals] };
      updated.plan.meals[idx] = r.data;
      setPlan(updated);
      toast.success(`Swapped to ${r.data.name}`);
    } catch (e) {
      toast.error("Swap failed");
    } finally {
      setSwapping(null);
    }
  };

  const dailyTotals = plan?.plan?.totals;
  const weekDays = weekPlan?.plan?.days || [];
  const currentDay = weekDays[activeDay];
  const groceryList = view === "week" ? weekPlan?.plan?.weekly_grocery_list : plan?.plan?.grocery_list;
  const prepTip = view === "week" ? weekPlan?.plan?.meal_prep_tip : plan?.plan?.meal_prep_tip;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline text-blaze">/ Nutrition</div>
          <h1 className="font-heading text-5xl uppercase mt-1">
            {view === "day" ? "Today's plate." : "Weekly plan."}
          </h1>
          {!isPremium && (
            <p className="text-[11px] uppercase tracking-widest text-yellow-500 mt-2 inline-flex items-center gap-1.5">
              <Lock className="h-3 w-3" /> AI meal planning is a Premium feature · 1 taster plan on free
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            data-testid={MEALS.generate}
            onClick={generateDay}
            disabled={loading}
            className="blaze-btn px-4 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="h-3 w-3" /> {loading && view === "day" ? "..." : "1-day plan"}
          </button>
          <button
            data-testid={WEEK.generateWeek}
            onClick={generateWeek}
            disabled={loading}
            className="ghost-btn px-4 py-2.5 text-xs inline-flex items-center gap-2 disabled:opacity-50"
          >
            <CalendarRange className="h-3 w-3" /> {loading && view === "week" ? "..." : "7-day plan"}
          </button>
        </div>
      </div>

      {!isPremium && (plan || weekPlan) && (
        <div className="obs-card border-yellow-500/30 bg-yellow-500/5" data-testid="meals-upsell-banner">
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-lg uppercase">You've used your free taster plan</div>
              <p className="text-sm text-zinc-400 mt-1">
                Upgrade to Premium for unlimited AI meal planning, macro-matched swaps, and weekly grocery lists.
              </p>
            </div>
            <Link
              to="/pricing"
              className="blaze-btn px-4 py-2 text-xs uppercase tracking-widest shrink-0"
              data-testid="meals-upgrade-cta"
            >
              Upgrade
            </Link>
          </div>
        </div>
      )}

      {/* Food preferences — likes/dislikes/allergies feed the AI generator */}
      <FoodPrefsEditor />

      {/* View toggle */}
      <div className="inline-flex border border-obsidian-600 rounded-sm overflow-hidden">
        <button
          data-testid={WEEK.toggleDay}
          onClick={() => setView("day")}
          className={`px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 ${
            view === "day" ? "bg-blaze text-white" : "text-zinc-400 hover:text-white"
          }`}
        >
          <CalendarDays className="h-3 w-3" /> Day
        </button>
        <button
          data-testid={WEEK.toggleWeek}
          onClick={() => setView("week")}
          className={`px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 ${
            view === "week" ? "bg-blaze text-white" : "text-zinc-400 hover:text-white"
          }`}
        >
          <CalendarRange className="h-3 w-3" /> Week
        </button>
      </div>

      {/* DAY view */}
      {view === "day" && (
        <>
          {!plan && !loading && (
            <div className="obs-card text-center py-16">
              <ChefHat className="h-12 w-12 text-blaze mx-auto mb-4" />
              <h3 className="font-heading text-3xl uppercase mb-2">No plan yet</h3>
              <p className="text-zinc-400 mb-6">Tap "1-day plan" to generate.</p>
            </div>
          )}
          {plan && (
            <>
              {dailyTotals && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="stat-tile"><div className="overline">Total kcal</div><div className="font-data text-3xl">{dailyTotals.calories}</div></div>
                  <div className="stat-tile"><div className="overline">Protein</div><div className="font-data text-3xl">{dailyTotals.protein_g}g</div></div>
                  <div className="stat-tile"><div className="overline">Carbs</div><div className="font-data text-3xl">{dailyTotals.carbs_g}g</div></div>
                  <div className="stat-tile"><div className="overline">Fat</div><div className="font-data text-3xl">{dailyTotals.fat_g}g</div></div>
                </div>
              )}
              <div data-testid={MEALS.list} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {plan.plan.meals?.map((m, i) => (
                  <MealCard
                    key={i}
                    meal={m}
                    index={i}
                    onSwap={() => swap(i, m)}
                    swapping={swapping === i}
                    testId={i === 0 ? MEALS.swap : undefined}
                  />
                ))}
              </div>
            </>
          )}
        </>
      )}

      {/* WEEK view */}
      {view === "week" && (
        <>
          {!weekPlan && !loading && (
            <div className="obs-card text-center py-16">
              <CalendarRange className="h-12 w-12 text-blaze mx-auto mb-4" />
              <h3 className="font-heading text-3xl uppercase mb-2">No weekly plan</h3>
              <p className="text-zinc-400 mb-6">Tap "7-day plan" to generate one.</p>
            </div>
          )}
          {weekPlan && weekDays.length > 0 && (
            <>
              <div className="flex flex-wrap gap-2">
                {weekDays.map((d, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveDay(i)}
                    className={`px-4 py-2 text-xs uppercase tracking-widest border rounded-sm transition-colors ${
                      activeDay === i ? "border-blaze bg-blaze text-white" : "border-obsidian-600 text-zinc-400 hover:border-blaze hover:text-white"
                    }`}
                  >
                    {d.day}
                  </button>
                ))}
              </div>

              {currentDay && (
                <>
                  {currentDay.totals && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="stat-tile"><div className="overline">kcal</div><div className="font-data text-3xl">{currentDay.totals.calories}</div></div>
                      <div className="stat-tile"><div className="overline">Protein</div><div className="font-data text-3xl">{currentDay.totals.protein_g}g</div></div>
                      <div className="stat-tile"><div className="overline">Carbs</div><div className="font-data text-3xl">{currentDay.totals.carbs_g}g</div></div>
                      <div className="stat-tile"><div className="overline">Fat</div><div className="font-data text-3xl">{currentDay.totals.fat_g}g</div></div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {currentDay.meals?.map((m, i) => (
                      <MealCard key={i} meal={m} index={i} hideSwap />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Grocery / prep tip (shared) */}
      {groceryList && groceryList.length > 0 && (
        <div className="obs-card">
          <div className="flex items-center gap-3 mb-3">
            <ShoppingCart className="h-5 w-5 text-blaze" />
            <div>
              <div className="overline">{view === "week" ? "Weekly grocery list" : "Grocery list"}</div>
              {view === "week" && <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-0.5">Each item once · total for the week</div>}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-zinc-300">
            {groceryList.map((g, i) => {
              // Backend now returns weekly groceries as {item, total, qty_g}.
              // Daily / legacy entries are still plain strings.
              const isObj = typeof g === "object" && g !== null;
              const name = isObj ? g.item : g;
              const total = isObj ? g.total : null;
              return (
                <div key={i} className="flex items-center justify-between gap-3 border-b border-obsidian-600/50 py-1">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-blaze">▸</span>
                    <span className="truncate">{name}</span>
                  </div>
                  {total && (
                    <span className="text-[11px] text-zinc-400 font-data uppercase tracking-widest shrink-0">
                      {total}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {prepTip && (
        <div className="obs-card">
          <div className="overline mb-2">Meal-prep tip</div>
          <p className="text-zinc-300 leading-relaxed">{prepTip}</p>
        </div>
      )}
    </div>
  );
}

function MealCard({ meal, index, onSwap, swapping, hideSwap, testId }) {
  return (
    <div className="obs-card animate-fade-up">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="overline">Meal {index + 1}</div>
          <h3 className="font-heading text-2xl uppercase">{meal.name}</h3>
        </div>
        {!hideSwap && (
          <button
            data-testid={testId}
            onClick={onSwap}
            disabled={swapping}
            className="ghost-btn px-3 py-1.5 text-[10px] inline-flex items-center gap-1.5"
          >
            <RefreshCw className={`h-3 w-3 ${swapping ? "animate-spin" : ""}`} /> Swap
          </button>
        )}
      </div>
      <ul className="text-sm text-zinc-300 space-y-0.5 mb-3">
        {meal.items?.map((it, k) => (
          <li key={k} className="flex justify-between">
            <span>{it.food}</span>
            <span className="text-zinc-500">{it.qty}</span>
          </li>
        ))}
      </ul>
      <div className="grid grid-cols-4 gap-2 text-center text-xs mb-3">
        <div><div className="overline">kcal</div><div className="font-data text-base">{meal.calories}</div></div>
        <div><div className="overline">P</div><div className="font-data text-base text-blaze">{meal.protein_g}</div></div>
        <div><div className="overline">C</div><div className="font-data text-base">{meal.carbs_g}</div></div>
        <div><div className="overline">F</div><div className="font-data text-base">{meal.fat_g}</div></div>
      </div>
      {meal.prep_minutes && (
        <div className="text-xs text-zinc-500 mb-2 uppercase tracking-widest">{meal.prep_minutes} min · prep</div>
      )}
      {meal.recipe && <p className="text-xs text-zinc-400 leading-relaxed">{meal.recipe}</p>}
    </div>
  );
}
